import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { t, triggerTrackingUrl } from '@insight/toolkit-utils';
import { AddToCartModal, AddItemToCartModal } from '../../index';
import { BetterTogetherNewList } from './BetterTogetherNewList';

const PRODUCT_BETTER_TOGETHER = 'Better Together';

const getProductInfo = (product) => {
  const {
    materialId,
    description,
    clickTrackingURL,
    price: productPrice,
  } = product;
  const { listPrice, price, priceInclVat } = productPrice;
  return {
    materialId,
    description,
    descriptions: { shortDescription: description },
    quantity: 1,
    clickTrackingURL,
    price: {
      price: parseFloat(price),
      listPrice: parseFloat(listPrice),
      webPrice: parseFloat(price),
      webPriceInclVat: parseFloat(priceInclVat),
    },
  };
};

const ProductBetterTogetherView = ({
  addToCart,
  better_together,
  isLoggedIn,
  product,
  showVAT,
  permissions,
  webGroupPermissions,
  setMiniPDP,
  webGroupId,
  showPrice,
  loginLink,
  accountLink,
}) => {
  const { enableViewPricing } = permissions;
  const callForPrice = product?.price?.callForPrice;
  const inStock = product?.availability?.totalQuantity;
  const materialId = product?.materialId;
  if (!enableViewPricing) {
    return null;
  }
  const [isShoppingReqWGEnabled, setIsShoppingReqWGEnabled] = useState(false);
  useEffect(() => {
    (async () => {
      setIsShoppingReqWGEnabled(
        window?.flags?.['GNA-11686-CES-SHOPPING-CART']?.indexOf(webGroupId) !==
          -1
      );
    })();
  }, []);
  // Prevent rendering when product is out of stock or when placement list is less than 2.
  if (
    !better_together?.length ||
    better_together.length < 2 ||
    !inStock ||
    callForPrice
  )
    return null;
  const isStockAndPriceDisabled = webGroupPermissions.disableStockAndPrice;
  const [addToCartState, setAddToCartState] = useState({
    loading: false,
    fulfilled: false,
    rejected: null,
  });

  // Sort products making sure the current product is always the first item in the list and limit list to a maximum of 3 products
  const betterTogetherList = useMemo(
    () =>
      better_together
        .sort((a, b) =>
          a.materialId === materialId ? -1 : b.materialId === materialId ? 1 : 0
        )
        .slice(0, 4),
    [better_together, materialId]
  );

  const [selectedProducts, setSelectedProducts] = useState(() =>
    // Lazy load default state as an object using material id as the keys
    betterTogetherList.reduce(
      (obj, cur) => ({
        ...obj,
        [cur.materialId]: {
          ...getProductInfo(cur),
          image: cur.image,
          manufacturerPartNumber: cur.manufacturerPartNumber,
        },
      }),
      {}
    )
  );

  const currencyCode = better_together[0].price.currency;

  // Create an array of products to add to cart
  const productsToAdd = Object.values(selectedProducts);

  const addProduct = (product) => {
    setSelectedProducts({
      ...selectedProducts,
      [product.materialId]: getProductInfo(product),
    });
  };
  const removeProduct = (product) => {
    const copy = { ...selectedProducts };
    delete copy[product.materialId];
    setSelectedProducts(copy);
  };
  const addItemsToCart = async () => {
    // Get the values of the selected products from the selectedProducts state
    if (!productsToAdd?.length) return null;
    try {
      productsToAdd.forEach((prod) => {
        triggerTrackingUrl(prod.clickTrackingURL);
      });

      setAddToCartState({ loading: true, fulfilled: false, rejected: false });
      await addToCart(productsToAdd, isLoggedIn, true, PRODUCT_BETTER_TOGETHER);
      setAddToCartState({ loading: false, fulfilled: true, rejected: false });
    } catch (err) {
      setAddToCartState({
        loading: false,
        fulfilled: false,
        rejected: err.message,
      });
    }
  };

  const addToCartModal = () => {
    if (!addToCartState.fulfilled) return;
    if (!permissions.enable_purchasing_popup) {
      window.location = '/insightweb/viewCart';
      return;
    }
    return window && window?.flags?.['GNA-11886-ADD-TO-CART-MODAL'] ? (
      <AddItemToCartModal
        currencyCode={product.price?.currency}
        isLoggedIn={isLoggedIn}
        isOpen={addToCartState.fulfilled}
        onClose={() => {
          setAddToCartState({ ...addToCartState, fulfilled: false });
        }}
        isShoppingReqWGEnabled={isShoppingReqWGEnabled}
        products={productsToAdd}
        addToCart={() => {}}
        showPrice={!isStockAndPriceDisabled}
      />
    ) : (
      <AddToCartModal
        hideTabs
        isLoggedOutDefault={!isLoggedIn}
        showDividers
        isOpen={addToCartState.fulfilled}
        products={productsToAdd}
        currencyCode={product.price?.currency}
        onClose={() => {
          setAddToCartState({ ...addToCartState, fulfilled: false });
        }}
        showPrice={!isStockAndPriceDisabled}
      />
    );
  };

  return (
    <section className="c-product-better-together">
      <h5 className="c-product-better-together__title">
        {t('Better together')}
      </h5>
      <BetterTogetherNewList
        betterTogetherList={betterTogetherList}
        selectedProducts={selectedProducts}
        materialId={materialId}
        addToCartState={addToCartState}
        removeProduct={removeProduct}
        addProduct={addProduct}
        addItemsToCart={addItemsToCart}
        currency={currencyCode}
        isLoggedIn={isLoggedIn}
        showVAT={showVAT}
        setMiniPDP={setMiniPDP}
        webGroupPermissions={webGroupPermissions}
        showPrice={showPrice}
        loginLink={loginLink}
        accountLink={accountLink}
      />
      {addToCartModal()}
    </section>
  );
};

export default ProductBetterTogetherView;

ProductBetterTogetherView.propTypes = {
  product: PropTypes.object,
  addToCart: PropTypes.func,
  better_together: PropTypes.object,
  permissions: PropTypes.object,
  isIPSUser: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  showVAT: PropTypes.bool,
  webGroupPermissions: PropTypes.object,
  setMiniPDP: PropTypes.func,
  webGroupId: PropTypes.string,
  showPrice: PropTypes.bool,
  loginLink: PropTypes.string,
  accountLink: PropTypes.string,
};

ProductBetterTogetherView.defaultProps = {
  product: {},
  addToCart: () => {},
  better_together: {},
  permissions: {
    enable_warranty_purchase: true,
    enable_accessory_purchase: true,
    enable_purchasing_popup: true,
    enable_personal_product_list: false,
    enable_inventory_search: false,
    enableViewPricing: true,
  },
  isIPSUser: false,
  isLoggedIn: false,
  showVAT: false,
  webGroupPermissions: {
    enableListPrice: true,
    disableStockAndPrice: false,
  },
  setMiniPDP: () => {},
  webGroupId: '',
  showPrice: true,
};
